import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { TOUCH, colour, radius, shadow, space, text } from '../design/tokens.ts';
import { useMe } from './me.tsx';
import type { MenuItem } from './me.tsx';
import { pathFor } from './router.tsx';

/**
 * Screen 20: the dark bar across the top of every screen.
 *
 * The role rules are the server's, not the bar's. `/api/me` returns the tabs
 * and the More menu already filtered, so a viewer never has a Share links item
 * to hide and the bar cannot drift from what the routes actually allow.
 *
 * Under about 1200px the tabs fold into a drawer; below 720px it becomes the
 * phone bar — icon, project, search, count, menu. Menus close on Escape and on
 * a click outside, and carry `aria-expanded` and `aria-current`.
 */

/**
 * 64 by default, not 32. The mark's teal dot is a disc the same radius as
 * the stroke, sitting on the stroke's round cap with a white ring between
 * them. At 32 that ring is about one pixel and the dot reads as a smudge on
 * a white letter; at 64 it is a dot.
 */
export function MaestroMark({ size = 64 }: { size?: number }) {
  const id = `maestro-mark-${size}`;
  return (
    <svg width={size} height={size} viewBox="0 0 512 512" aria-hidden="true" style={{ flex: 'none' }}>
      <defs>
        <linearGradient id={id} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor={colour.iconFrom} />
          <stop offset="1" stopColor={colour.iconTo} />
        </linearGradient>
      </defs>
      <rect width="512" height="512" rx="112" fill={`url(#${id})`} />
      <rect
        x="3"
        y="3"
        width="506"
        height="506"
        rx="109"
        fill="none"
        stroke="#ffffff"
        strokeOpacity="0.22"
        strokeWidth="6"
      />
      <path
        d="M112 392 L112 132 L256 302 L400 132 L400 392"
        fill="none"
        stroke="#ffffff"
        strokeWidth="62"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="400" cy="392" r="62" fill={colour.iconDot} stroke="#ffffff" strokeWidth="18" />
    </svg>
  );
}

/** Closes a menu on Escape and on a click anywhere else. */
function useDismiss(open: boolean, close: () => void) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') close();
    };
    const onDown = (e: MouseEvent): void => {
      if (ref.current && !ref.current.contains(e.target as Node)) close();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open, close]);
  return ref;
}

function useWidth(): number {
  const [width, setWidth] = useState(() => window.innerWidth);
  useEffect(() => {
    const onResize = (): void => setWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return width;
}

const barButton = (on = false): React.CSSProperties => ({
  whiteSpace: 'nowrap',
  minHeight: TOUCH,
  padding: '0 12px',
  border: 'none',
  borderRadius: radius.control,
  background: on ? colour.barRaised : 'transparent',
  color: colour.barInk,
  font: text.smallStrong.font,
  cursor: 'pointer',
  ...(on ? { boxShadow: `inset 0 -2px 0 ${colour.iconDot}` } : {})
});

function Menu({
  label,
  open,
  onToggle,
  children,
  align = 'left'
}: {
  label: string;
  open: boolean;
  onToggle: () => void;
  children: ReactNode;
  align?: 'left' | 'right';
}) {
  const ref = useDismiss(open, onToggle);
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button type="button" onClick={onToggle} aria-expanded={open} style={barButton(open)}>
        {label} <span aria-hidden="true" style={{ fontSize: 10 }}>▼</span>
      </button>
      {open && (
        <div
          role="menu"
          style={{
            position: 'absolute',
            top: 52,
            ...(align === 'right' ? { right: 0 } : { left: 0 }),
            zIndex: 20,
            width: 300,
            boxSizing: 'border-box',
            padding: space.tight,
            background: colour.surface,
            color: colour.ink,
            border: `1px solid ${colour.edge}`,
            borderRadius: radius.card,
            boxShadow: shadow.float,
            display: 'flex',
            flexDirection: 'column',
            gap: 2
          }}
        >
          {children}
        </div>
      )}
    </div>
  );
}

function MenuRow({
  children,
  onClick,
  tone
}: {
  children: ReactNode;
  onClick: () => void;
  tone?: 'accent';
}) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      style={{
        minHeight: TOUCH,
        padding: `0 ${space.tight}px`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: space.tight,
        border: 'none',
        borderRadius: radius.item,
        background: 'transparent',
        color: tone === 'accent' ? colour.link : colour.ink,
        font: tone === 'accent' ? text.smallStrong.font : text.small.font,
        textAlign: 'left',
        cursor: 'pointer',
        width: '100%'
      }}
    >
      {children}
    </button>
  );
}

function MenuHeading({ children }: { children: ReactNode }) {
  return (
    <span style={{ padding: `6px ${space.tight}px`, ...text.label, color: colour.inkMuted }}>
      {children}
    </span>
  );
}

function Divider() {
  return <div style={{ height: 1, background: colour.edgeSoft, margin: '4px 0' }} />;
}

export interface MachineSummary {
  on: number;
  total: number;
  flagged: { host: string; queued: number }[];
}

export function MenuBar({
  projectId,
  projects,
  screen,
  onGo,
  inboxCount,
  machines,
  onSearch
}: {
  projectId: string | null;
  projects: { project_id: string; name: string; note?: string }[];
  screen: string;
  onGo: (to: string) => void;
  inboxCount: number | null;
  machines: MachineSummary | null;
  onSearch: () => void;
}) {
  const state = useMe();
  const width = useWidth();
  const [open, setOpen] = useState<string | null>(null);
  const toggle = (key: string) => () => setOpen((held) => (held === key ? null : key));
  const close = (): void => setOpen(null);

  const answer = state.status === 'ready' ? state.answer : null;
  const tabs: MenuItem[] = answer?.menu.tabs ?? [];
  const more: MenuItem[] = answer?.menu.more ?? [];
  const project = projects.find((p) => p.project_id === projectId);

  const narrow = width < 1200;
  const phone = width < 720;

  const goScreen = (key: string) => (): void => {
    close();
    // Admin and Help are about Maestro rather than a project, so they have a
    // path of their own. Without this they bounced to the project list, which
    // on a fresh deployment is empty — and Admin settings was unreachable.
    if (key === 'admin' || key === 'help') {
      onGo(`/${key}`);
      return;
    }
    if (!projectId) {
      onGo('/projects');
      return;
    }
    onGo(pathFor(projectId, key));
  };

  return (
    <nav
      aria-label="Main"
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 30,
        display: 'flex',
        alignItems: 'center',
        gap: space.hair,
        padding: `0 ${phone ? 12 : 16}px`,
        // Tall enough for a 64px mark, which is the smallest the dot reads at.
        minHeight: 76,
        background: colour.barBg,
        color: colour.barInk,
        whiteSpace: 'nowrap'
      }}
    >
      <button
        type="button"
        onClick={() => onGo('/projects')}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          paddingRight: 12,
          minHeight: TOUCH,
          border: 'none',
          background: 'transparent',
          color: colour.barInk,
          cursor: 'pointer'
        }}
      >
        <MaestroMark size={phone ? 44 : 64} />
        {!phone && (
          <span style={{ font: "600 18px/24px 'IBM Plex Sans', system-ui, sans-serif", letterSpacing: '-0.02em' }}>
            Maestro
          </span>
        )}
      </button>

      {/* Project switcher */}
      <Menu
        label={project?.name ?? (projectId ?? 'All projects')}
        open={open === 'project'}
        onToggle={toggle('project')}
      >
        <MenuHeading>Your projects</MenuHeading>
        {projects.map((p) => (
          <MenuRow
            key={p.project_id}
            onClick={() => {
              close();
              onGo(pathFor(p.project_id, 'board'));
            }}
          >
            <span>{p.name}</span>
            {p.note && (
              <span style={{ ...text.label, color: colour.inkMuted, fontWeight: 600 }}>{p.note}</span>
            )}
          </MenuRow>
        ))}
        <Divider />
        <MenuRow
          onClick={() => {
            close();
            onGo('/projects');
          }}
        >
          All projects
        </MenuRow>
        {answer?.can.new_project && (
          <MenuRow
            tone="accent"
            onClick={() => {
              close();
              onGo('/projects/new');
            }}
          >
            New project
          </MenuRow>
        )}
      </Menu>

      {/* Tabs, or a drawer when there is not room */}
      {!narrow &&
        tabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={goScreen(tab.key)}
            {...(screen === tab.key ? { 'aria-current': 'page' as const } : {})}
            style={barButton(screen === tab.key)}
          >
            {tab.says}
          </button>
        ))}

      {narrow && (
        <Menu label="Screens" open={open === 'drawer'} onToggle={toggle('drawer')}>
          {tabs.map((tab) => (
            <MenuRow key={tab.key} onClick={goScreen(tab.key)}>
              <span>{tab.says}</span>
              {screen === tab.key && <span style={{ ...text.label, color: colour.inkMuted }}>here</span>}
            </MenuRow>
          ))}
          <Divider />
          {more.map((item) => (
            <MenuRow key={item.key} onClick={goScreen(item.key)}>
              {item.says}
            </MenuRow>
          ))}
        </Menu>
      )}

      {!narrow && more.length > 0 && (
        <Menu label="More" open={open === 'more'} onToggle={toggle('more')}>
          {more.map((item) => (
            <MenuRow key={item.key} onClick={goScreen(item.key)}>
              {item.says}
            </MenuRow>
          ))}
        </Menu>
      )}

      <span style={{ flex: 1 }} />

      <button type="button" onClick={onSearch} aria-label="Search" style={barButton()}>
        {phone ? '⌕' : 'Search ⌘K'}
      </button>

      {/* Machines: how many are on, and anything stuck behind one that is not */}
      {machines && !phone && (
        <Menu
          label={`${machines.on} of ${machines.total} on`}
          open={open === 'machines'}
          onToggle={toggle('machines')}
          align="right"
        >
          <MenuHeading>Machines</MenuHeading>
          <MenuRow onClick={goScreen('runs')}>
            <span>
              {machines.on} of {machines.total} reporting
            </span>
          </MenuRow>
          {machines.flagged.map((m) => (
            <MenuRow key={m.host} onClick={goScreen('runs')}>
              <span>{m.host}</span>
              <span style={{ ...text.label, color: colour.staleInk, fontWeight: 600 }}>
                {m.queued} waiting
              </span>
            </MenuRow>
          ))}
        </Menu>
      )}

      <button
        type="button"
        onClick={goScreen('inbox')}
        style={{
          ...barButton(screen === 'inbox'),
          display: 'flex',
          alignItems: 'center',
          gap: 6
        }}
      >
        {!phone && 'Needs you'}
        {inboxCount != null && inboxCount > 0 && (
          <span
            style={{
              minWidth: 22,
              padding: '1px 6px',
              borderRadius: radius.pill,
              background: colour.stale,
              color: '#ffffff',
              ...text.label,
              fontWeight: 600,
              textAlign: 'center'
            }}
          >
            {inboxCount}
          </span>
        )}
      </button>

      {/* Account */}
      <Menu
        label={answer ? (phone ? '☰' : answer.me.name) : '…'}
        open={open === 'account'}
        onToggle={toggle('account')}
        align="right"
      >
        {answer && (
          <>
            <MenuHeading>
              {answer.me.name} · {answer.labels[answer.me.role] ?? answer.me.role}
            </MenuHeading>
            {answer.me.role_from === 'group' && (
              <span style={{ padding: `0 ${space.tight}px`, ...text.note, color: colour.staleInk }}>
                From your Cognito group. Maestro has no record for you yet, so you cannot mark
                results good.
              </span>
            )}
            {answer.me.role_disagreement && (
              <span style={{ padding: `0 ${space.tight}px`, ...text.note, color: colour.inkSoft }}>
                {answer.me.role_disagreement}
              </span>
            )}
            <Divider />
            <MenuRow onClick={goScreen('search')}>Saved filters</MenuRow>
            <MenuRow onClick={goScreen('help')}>Help and glossary</MenuRow>
            {answer.can.admin && <MenuRow onClick={goScreen('admin')}>Admin settings</MenuRow>}
            <Divider />
            <MenuRow
              onClick={() => {
                close();
                state.signOut();
              }}
            >
              Sign out
            </MenuRow>
          </>
        )}
      </Menu>
    </nav>
  );
}

/**
 * The row under the bar: where you are, and the one primary action for this
 * screen. A viewer is told why there is not one, rather than being left to
 * wonder where the button went.
 */
export function ContextRow({
  crumbs,
  action,
  instead
}: {
  crumbs: { label: string; to?: string }[];
  action?: ReactNode;
  instead?: string | null;
}) {
  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: space.control,
        padding: `10px clamp(16px, 4vw, 64px)`,
        background: colour.surface,
        borderBottom: `1px solid ${colour.edge}`
      }}
    >
      <nav aria-label="Breadcrumb" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6 }}>
        {crumbs.map((crumb, i) => (
          <span key={`${crumb.label}-${i}`} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {i > 0 && <span style={{ ...text.note, color: colour.inkFaint }}>/</span>}
            <span style={{ ...text.small, color: i === crumbs.length - 1 ? colour.ink : colour.inkSoft }}>
              {crumb.label}
            </span>
          </span>
        ))}
      </nav>
      {action ?? (instead && <span style={{ ...text.small, color: colour.inkMuted }}>{instead}</span>)}
    </div>
  );
}
